import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useLabels } from 'wsm-common-data';

import { enhanceHighlightedAttributes } from '../../../utilities/highlightedAttributes';
import { useVehicleTabs } from '../../../hooks/useVehicleTabs';
import vehiclePropType from '../../../propTypes/vehiclePropType';
import VehicleAttributeList from './VehicleAttributeList';
import PricingElement from './PricingElement';
import GridView from './layoutView/GridView';
import ListView from './layoutView/ListView';
import DetailedIncentivesContainer from './DetailedIncentivesContainer';

const Details = ({
	vehicle,
	incentives,
	accounts,
	isDisplayingEPrice,
	setIsDisplayingEPrice,
	showExpandedPricing,
	setShowExpandedPricing,
	showNewCarBoostPaymentsInDetails
}) => {
	const { layout, isSmallScreen } = useSelector((state) => state.layout);
	const { accountId } = useSelector((state) => state.widgetInfo);
	const labels = useLabels();

	const highlightedAttributes = enhanceHighlightedAttributes(
		vehicle,
		accounts
	);
	const containerRef = useRef();

	useEffect(() => {
		const tablist = containerRef.current.querySelector('[role=tablist]');
		if (!tablist) {
			return;
		}
		tablist.setAttribute('aria-label', labels.get('VEHICLE_SUMMARY'));
	}, [labels]);

	const accountInfo = accounts[vehicle.accountId] || {};

	const tabComponents = {
		info: <VehicleAttributeList vehicle={vehicle} />,
		specials: (
			<DetailedIncentivesContainer
				accountInfo={accountInfo}
				vehicle={vehicle}
				incentives={incentives}
			/>
		),
		pricing: (
			<PricingElement
				accountId={accountId}
				accountInfo={accountInfo}
				incentives={incentives}
				isDisplayingEPrice={isDisplayingEPrice}
				setShowExpandedPricing={setShowExpandedPricing}
				showExpandedPricing={showExpandedPricing}
				showNewCarBoostPaymentsInDetails={
					showNewCarBoostPaymentsInDetails
				}
				vehicle={vehicle}
			/>
		)
	};

	const { tabArray, tabLabels } = useVehicleTabs(tabComponents);

	return (
		<div ref={containerRef} className="h-100 d-flex flex-column">
			{layout === 'list' && !isSmallScreen ? (
				<ListView
					accountId={accountId}
					accountInfo={accountInfo}
					highlightedAttributes={highlightedAttributes}
					incentives={incentives}
					isDisplayingEPrice={isDisplayingEPrice}
					setIsDisplayingEPrice={setIsDisplayingEPrice}
					setShowExpandedPricing={setShowExpandedPricing}
					showExpandedPricing={showExpandedPricing}
					showNewCarBoostPaymentsInDetails={
						showNewCarBoostPaymentsInDetails
					}
					vehicle={vehicle}
				/>
			) : (
				<GridView
					accountId={accountId}
					accountInfo={accountInfo}
					highlightedAttributes={highlightedAttributes}
					incentives={incentives}
					isDisplayingEPrice={isDisplayingEPrice}
					setIsDisplayingEPrice={setIsDisplayingEPrice}
					setShowExpandedPricing={setShowExpandedPricing}
					showExpandedPricing={showExpandedPricing}
					showNewCarBoostPaymentsInDetails={
						showNewCarBoostPaymentsInDetails
					}
					tabArray={tabArray}
					tabLabels={tabLabels}
					vehicle={vehicle}
				/>
			)}
		</div>
	);
};

Details.propTypes = {
	accounts: PropTypes.shape({}),
	incentives: PropTypes.shape({}),
	isDisplayingEPrice: PropTypes.bool.isRequired,
	setIsDisplayingEPrice: PropTypes.func.isRequired,
	setShowExpandedPricing: PropTypes.func.isRequired,
	showExpandedPricing: PropTypes.bool.isRequired,
	showNewCarBoostPaymentsInDetails: PropTypes.bool,
	vehicle: vehiclePropType.isRequired
};

Details.defaultProps = {
	incentives: {},
	accounts: {}
};

export default Details;
